import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import { Mic, CheckCircle2, Zap, Terminal, AlertCircle } from 'lucide-react';

export interface VoiceTypingNodeData {
  enableVoiceTyping?: boolean;
  targetFormSelector?: string;
  autoSubmitOnConfirmation?: boolean;
  liveTypingTelemetry?: boolean;
  [key: string]: unknown;
}

export const VoiceTypingNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as VoiceTypingNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof VoiceTypingNodeData, value: any, allUpdates?: Partial<VoiceTypingNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const res = await fetch('/api/admin/luna-flows/save-node-data', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'VOICE_TYPING',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[VoiceTypingNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof VoiceTypingNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);
  const isEnabled = nodeData.enableVoiceTyping !== false;

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[380px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#140C22]/98 via-[#0A0612]/98 to-[#05030A]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-purple-400 ring-2 ring-purple-400/50 shadow-[0_0_35px_rgba(168,85,247,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-purple-500/40 hover:border-purple-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(168,85,247,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump Toolbar ── */}
      <div className="absolute -top-11 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-auto z-50 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-950/95 backdrop-blur-xl border border-purple-500/40 shadow-[0_0_25px_rgba(168,85,247,0.4)]">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openPortalData?.('voice_typing', id);
          }}
          className="nodrag flex items-center gap-1.5 text-[11px] font-bold text-purple-300 hover:text-purple-200 transition-colors cursor-pointer"
        >
          <Mic size={13} className="text-purple-400 animate-pulse" />
          <span>Voice Typing Studio</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-purple-500/20 text-purple-300 font-mono border border-purple-500/30">
            HUD ↗
          </span>
        </button>
      </div>

      {/* ── Directional Connection Handles ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-voice-typing"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Input from Luna Core Hub"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-voice-typing"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Output: Chained Execution Action"
      />

      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(168,85,247,0.8)]"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-950/80 via-indigo-950/60 to-slate-950/80 px-4 py-3 border-b border-purple-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-purple-500/20 border border-purple-500/40 text-purple-300 shadow-[0_0_12px_rgba(168,85,247,0.3)]">
            <Mic size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Voice-Typing Node</h4>
            <p className="text-[10px] text-purple-300/70 font-mono">Autonomous Form Filling</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-purple-400 animate-pulse bg-purple-500/10 px-2 py-0.5 rounded-full border border-purple-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
            EXECUTION
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3.5 text-xs">
        {/* Toggle Switch */}
        <div className="p-2.5 rounded-xl bg-purple-950/40 border border-purple-500/20">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-[11px] font-bold text-slate-200 flex items-center gap-1.5">
              <Zap size={13} className="text-purple-400" /> Enable Voice-to-Text Form Filling
            </span>
            <input
              type="checkbox"
              className="nodrag w-4 h-4 rounded border-white/20 text-purple-600 focus:ring-purple-500 cursor-pointer accent-purple-500"
              checked={isEnabled}
              onChange={(e) => handleChange('enableVoiceTyping', e.target.checked)}
            />
          </label>
        </div>

        {/* Target Form ID Selector */}
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
            <Terminal size={12} className="text-purple-400" /> Target Form Container / Selector
          </label>
          <input
            type="text"
            className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400/30 transition-all placeholder:text-slate-600 font-mono font-bold"
            placeholder="e.g. #active-genui-form or default"
            value={nodeData.targetFormSelector || ''}
            onChange={(e) => handleChange('targetFormSelector', e.target.value)}
          />
        </div>

        {/* Tool Declarations Badge */}
        <div className="p-2.5 rounded-xl bg-slate-900/90 border border-white/5 space-y-1.5">
          <div className="text-[10px] font-black uppercase tracking-wider text-purple-300 flex items-center gap-1">
            <CheckCircle2 size={12} className="text-purple-400" /> Registered Function Declarations
          </div>
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-purple-500/20 text-purple-200 border border-purple-500/30 font-bold">
              fill_form_field(fieldName, value)
            </span>
            <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-purple-500/20 text-purple-200 border border-purple-500/30 font-bold">
              submit_form()
            </span>
          </div>
        </div>

        {/* Auto-Submit On Confirmation */}
        <div className="pt-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="nodrag w-3.5 h-3.5 rounded border-white/20 text-purple-600 focus:ring-purple-500 cursor-pointer accent-purple-500"
              checked={nodeData.autoSubmitOnConfirmation !== false}
              onChange={(e) => handleChange('autoSubmitOnConfirmation', e.target.checked)}
            />
            <span className="text-[10px] text-slate-400 font-medium">
              Auto-Submit Form Upon Voice Confirmation
            </span>
          </label>
        </div>

      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />
    </div>
  );
});

VoiceTypingNode.displayName = 'VoiceTypingNode';
export default VoiceTypingNode;
